import { Separator, Typography } from '@cannect/new-components/atoms'
import { TDigitalJourneyFormData } from '@cannect/pages/DigitalJourney/types'
import { formatToReal } from '@cannect/utils/number'
import { getSplitDeliveryEstimateTexts } from '@cannect/utils/deliveryEstimate'

type TCartSummary = {
  summaryFields: TDigitalJourneyFormData['cart']['summary']
  productsQuantity: number
  productsFields?: TDigitalJourneyFormData['cart']['products']
}

function formatEstimateDate(iso: string | null | undefined) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export const CartSummary = ({ summaryFields, productsQuantity, productsFields = [] }: TCartSummary) => {
  const hasRange = Boolean(summaryFields?.estimated_delivery_range_start && summaryFields?.estimated_delivery_range_end)

  const hasPillItem =
    typeof summaryFields?.has_pill_items === 'boolean'
      ? summaryFields.has_pill_items
      : productsFields.some((product) => product?.origin === 'pill')
  const isAllPillItems = productsFields.length > 0 && productsFields.every((product) => product?.origin === 'pill')
  const splitEstimateTexts = getSplitDeliveryEstimateTexts({
    hasPillItem,
    isAllPillItems,
    pillEstimate: summaryFields?.estimated_delivery_pill,
    othersEstimate: summaryFields?.estimated_delivery_others
  })

  return (
    <div className="grid grid-cols-5 gap-2">
      <Typography.Text type="captionOne" className="col-span-3">
        Subtotal (sem desconto)
      </Typography.Text>
      <Typography.Text type="captionOne" weight="semibold" className="col-span-2 text-right">
        {formatToReal(summaryFields?.amount_total)}
      </Typography.Text>
      {!summaryFields?.no_import_cost && (
        <Typography.Text type="captionOne" className="col-span-3">
          Taxa de Importação
        </Typography.Text>
      )}
      {!summaryFields?.no_import_cost && (
        <Typography.Text type="captionOne" className="col-span-2 text-right">
          {formatToReal(summaryFields?.shipping_amount)}
        </Typography.Text>
      )}
      <Typography.Text
        type="captionOne"
        className={`col-span-3 ${summaryFields?.discount_product > 0 ? 'text-primary-700' : ''}`}>
        Desconto nos Produtos
      </Typography.Text>
      <Typography.Text
        type="captionOne"
        className={`col-span-2 text-right ${summaryFields?.discount_product > 0 ? 'text-primary-700' : ''}`}>
        -{formatToReal(summaryFields?.discount_product)}
      </Typography.Text>
      {!summaryFields?.no_import_cost && (
        <Typography.Text type="captionOne" className="col-span-3">
          Desconto na Importação
        </Typography.Text>
      )}
      {!summaryFields?.no_import_cost && (
        <Typography.Text type="captionOne" className="col-span-2 text-right">
          -{formatToReal(summaryFields?.discount_freight)}
        </Typography.Text>
      )}
      <Separator className="col-span-full mt-4" />
      <Typography.Text className="col-span-3" weight="semibold">
        Total{' '}
        <span className="text-xs text-neutral-300">
          ({productsQuantity} {productsQuantity > 1 ? 'itens' : 'item'})
        </span>
      </Typography.Text>
      <Typography.Text weight="semibold" className="col-span-2 text-right">
        {formatToReal(summaryFields?.amount_liquid)}
      </Typography.Text>

      {(splitEstimateTexts || hasRange) && (
        <div className="col-span-full mt-3 rounded-lg border border-primary-200 bg-primary-50 p-3">
          <Typography.Text type="captionOne" weight="semibold" className="mb-1 text-primary-700">
            Estimativa de entrega
          </Typography.Text>
          {splitEstimateTexts ? (
            <div className="flex flex-col gap-1">
              {splitEstimateTexts.map((text) => (
                <Typography.Text key={text} type="captionTwo" className="text-neutral-700">
                  {text}
                </Typography.Text>
              ))}
            </div>
          ) : (
            <Typography.Text type="captionTwo" className="text-neutral-700">
              Entre {formatEstimateDate(summaryFields.estimated_delivery_range_start)} e{' '}
              {formatEstimateDate(summaryFields.estimated_delivery_range_end)}.
            </Typography.Text>
          )}
        </div>
      )}
    </div>
  )
}
